'use client';

import type { ReactNode} from 'react';
import React, { useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import { useForceUpdate } from '../../utils/force-update';

type Props = {
  type?: string;
  children?: ReactNode;
};

export const Portal = (props: Props) => {
  const { children, type = 'z-portal' } = props;

  const mountNode = useRef<HTMLDivElement | null>(null);
  const portalNode = useRef<HTMLElement | null>(null);
  const forceUpdate = useForceUpdate();

  useLayoutEffect(() => {
    if (!mountNode.current) return;
    const ownerDocument = mountNode?.current.ownerDocument;
    portalNode.current = ownerDocument?.createElement(type);
    ownerDocument?.body.appendChild(portalNode.current);
    forceUpdate();
    return () => {
      if (portalNode.current && portalNode.current.ownerDocument) {
        portalNode.current.ownerDocument.body.removeChild(portalNode.current);
      }
    };
  }, [type, forceUpdate]);

  return portalNode.current ? createPortal(children, portalNode.current) : <span ref={mountNode} />;
};
